'use client';

import type { LocalCartInfo } from '@/types';
import { atomWithStorage } from 'jotai/utils';

/**
 * ゲストカートの永続化レイヤ。
 *
 * このモジュールは `state/index.ts` からのみ参照する（dependency-cruiser で強制）。
 * 生の atom を直接使うと hydration 前の暫定値をロジックの入力にしてしまうため、
 * 用途別の公開 API（件数の購読 / スナップショット読み取り / 書き込み）経由に限定する。
 */

export const CART_STORAGE_KEY = 'cartInfo';

const initialCartInfo: LocalCartInfo = {
  cartVersion: Number(process.env.NEXT_PUBLIC_LOCAL_CART_VERSION || 1),
  items: [],
};

// カート情報のAtom(localStorage)
export const cartInfoAtom = atomWithStorage<LocalCartInfo>(CART_STORAGE_KEY, initialCartInfo);

/**
 * localStorage のカート情報を直接読み取る。
 * atomWithStorage はマウント前（初回レンダー）に初期値(items:[])を返すため、
 * ハードロード直後に実データが必要な処理はこの関数で直接読む（hydration 待ちで空になるのを防ぐ）。
 * @returns 保存済みカート情報。未保存・パース不能・SSR時は初期値
 */
export const readLocalCartInfo = (): LocalCartInfo => {
  if (typeof window === 'undefined') return initialCartInfo;
  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY);
    const parsed: LocalCartInfo | null = raw ? JSON.parse(raw) : null;
    return parsed ?? initialCartInfo;
  } catch {
    return initialCartInfo;
  }
};
